import { useLocationStore } from '@/stores/useLocationStore';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { DateSelectContent } from './DateSelectContent';

interface DatePickerProps {
    compact?: boolean;
    actionLabel?: string;
    ariaLabel?: string;
}

export const DatePicker = ({ compact = false, actionLabel, ariaLabel }: DatePickerProps) => {
    const { dateRange, dateMode } = useLocationStore();

    if (compact || actionLabel) {
        return (
            <Popover className="relative">
                <PopoverButton aria-label={ariaLabel} className="flex min-h-[44px] w-full items-center rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                    {actionLabel ? <span className="text-sm font-semibold text-primary underline underline-offset-4">{actionLabel}</span> : <span className={cn("text-xs text-foreground truncate", !dateRange.from && "text-muted-foreground/60")}>
                        {dateRange.from ? (
                            <>
                                {format(dateRange.from, 'MMM d')}
                                {dateRange.to ? ` - ${format(dateRange.to, 'MMM d')}` : ''}
                            </>
                        ) : dateMode === 'flexible' ? (
                            'No dates yet'
                        ) : (
                            "Dates"
                        )}
                    </span>}
                </PopoverButton>

                <PopoverPanel
                    anchor={{ to: 'bottom start', gap: 8 }}
                    className="z-50 w-80 max-w-[calc(100vw-2rem)] rounded-xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none [--anchor-max-height:80vh]"
                >
                    {({ close }) => <DateSelectContent onClose={close} deferChanges={!!actionLabel} />}
                </PopoverPanel>
            </Popover>
        );
    }

    return (
        <Popover className="relative">
            <PopoverButton className="flex min-h-[44px] w-full flex-col items-start rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                <span className="text-xs font-bold text-foreground uppercase tracking-wider">When</span>
                <span className={cn("text-sm text-muted-foreground", !dateRange.from && "text-muted-foreground/60")}>
                    {dateRange.from ? (
                        <>
                            {format(dateRange.from, 'MMM d')}
                            {dateRange.to ? ` - ${format(dateRange.to, 'MMM d')}` : ''}
                        </>
                    ) : (
                        "Add dates"
                    )}
                </span>
            </PopoverButton>

            <PopoverPanel
                anchor={{ to: 'bottom start', gap: 8 }}
                className="w-80 rounded-xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none [--anchor-max-height:80vh]"
            >
                {({ close }) => <DateSelectContent onClose={close} />}
            </PopoverPanel>
        </Popover>
    );
};
